import React, { useRef } from "react";
import { useEffect, useState, RefObject } from 'react';

export const useResizeObserver = (ref: RefObject<HTMLElement>) => {
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [ref]);

  return dimensions;
};

export enum DeviceTypes {
  Tablet,
  Phone,
  TV
}

export interface DeviceFrameProps {
  deviceType: DeviceTypes;
  src: string;
  className?: string;
  children?: React.ReactNode;
}

const DeviceFrame = ({ children, className, deviceType, src }: DeviceFrameProps) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const { width } = useResizeObserver(parentRef);

  let ratio = 1;

  switch (deviceType) {
    case DeviceTypes.Tablet:
      ratio = 1.33;
      break;
    case DeviceTypes.Phone:
      ratio = 9 / 19;
      break;
    case DeviceTypes.TV:
      ratio = 16 / 9;
      break;
  }

  const isLandscape = true;

  const style = isLandscape ? { height: width / ratio } : { height: width / ratio };

  return <div ref={parentRef} style={style}
    className={`device-frame ${DeviceTypes[deviceType].toLowerCase()} ${className ?? ""}`}>

    <div className="display" style={style}>
      {src &&
        <img src={src} alt={DeviceTypes[deviceType]} />}

      {children}
    </div>

    {deviceType === DeviceTypes.Tablet &&
      <img className="frame-image" src="/assets/images/cms/tablet-frame.png" alt="Tablet" />}

    {deviceType === DeviceTypes.Phone &&
      <img className="frame-image" src="/assets/images/cms/phone-frame.png" alt="Phone" />}

    {deviceType === DeviceTypes.TV &&
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 11">
        {/* TV Stand */}
        <path d="M154 5 L234 5 L239 11 L149 11 Z" fill="#2c2c2c" />

        {/* Power LED */}
        <circle cx="383" cy="4" r="1" fill="#00ee00" opacity="0.8" />
      </svg>}
  </div>;
}

export default DeviceFrame;